import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import CakeCard from '../components/CakeCard'; // Import CakeCard component
import { getImageUrl } from '../data/cakes';
import { api } from '../utils/api';
import Toast from '../components/Toast';
import Footer from '../components/Footer';
import './CakeDetails.css';

const CakeDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addCake } = useCart();
  
  const [cake, setCake] = useState(null);
  const [weight, setWeight] = useState('');
  const [flavour, setFlavour] = useState('');
  const [quantity] = useState(1);
  const [cakeMessage, setCakeMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState(null);
  const [relatedCakes, setRelatedCakes] = useState([]); // State for related cakes
  const [debugError, setDebugError] = useState(null); // Just in case we need it, though normally unused here

  useEffect(() => {
    window.scrollTo(0, 0);
    loadCake();
  }, [id]);

  // Set default weight and flavour when cake loads
  useEffect(() => {
    if (cake) {
      // Set default weight to base_weight
      if (cake.base_weight) {
        setWeight(parseFloat(cake.base_weight).toString());
      }
      
      // Set default flavour to first available flavour
      if (cake.flavours && cake.flavours.length > 0) {
        setFlavour(JSON.stringify(cake.flavours[0]));
      }
    }
  }, [cake]);

  const loadCake = async () => {
    setLoading(true);
    try {
      // Fetch all cakes since there's no single cake endpoint
      const data = await api.get('/cakes.php');
      
      if (data.success && data.data && data.data.cakes) {
        // Find the specific cake by ID
        const foundCake = data.data.cakes.find(c => c.id === parseInt(id));
        
        if (foundCake) {
          setCake(foundCake);
        } else {
          console.error('Cake not found');
          navigate('/');
        }
      } else {
        console.error('Failed to load cakes');
        navigate('/');
      }
    } catch (error) {
      console.error('Error loading cake:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // Fetch related cakes when cake is loaded
  useEffect(() => {
    const fetchRelated = async () => {
      if (!cake) return;
      
      try {
        const data = await api.get('/cakes.php');
        if (data.success && data.data && data.data.cakes) {
          // Filter cakes by same category, excluding current cake
          const related = data.data.cakes.filter(c => 
            c.category_id === cake.category_id && 
            c.id !== cake.id
          );
          // Shuffle and take top 6
          const shuffled = related.sort(() => 0.5 - Math.random());
          setRelatedCakes(shuffled.slice(0, 6));
        }
      } catch (err) {
        console.error('Error fetching related cakes:', err);
      }
    };

    fetchRelated();
  }, [cake]);

  const handleRelatedCakeClick = (relatedCake) => {
    navigate(`/cake/${relatedCake.id}`);
    window.scrollTo(0, 0);
  };


  const calculatePrice = () => {
    if (!cake) return 0;
    
    const basePrice = parseFloat(cake.base_price) || 0;
    const baseWeight = parseFloat(cake.base_weight) || 1;
    const customFee = parseFloat(cake.custom_design_fee) || 0;
    
    if (!weight || !flavour) {
      return basePrice + customFee;
    }

    // Calculate cake price: base price for base weight, 75% increase per 500gm above base
    const selectedWeight = parseFloat(weight);
    
    let cakePrice = basePrice; // Start with base price
    
    // Only apply increment if weight is more than base weight
    if (selectedWeight > baseWeight) {
      cakePrice = basePrice + (((selectedWeight - baseWeight) / 0.5) * (((basePrice / baseWeight) / 2) * 0.75));
    } else if (selectedWeight < baseWeight) {
      // If less than base weight, scale down proportionally
      cakePrice = basePrice * (selectedWeight / baseWeight);
    }
    
    const selectedFlavour = JSON.parse(flavour);
    // For special cakes, we do not include flavour price based on weight
    const flavourPrice = (cake.is_special === 1) ? 0 : (parseFloat(selectedFlavour.price_per_half_kg) * (selectedWeight / 0.5));
    
    return cakePrice + customFee + flavourPrice;
  };

  const getPriceBreakdown = () => {
    if (!cake) return { base: 0, customFee: 0, flavour: 0 };
    
    const basePrice = parseFloat(cake.base_price) || 0;
    const baseWeight = parseFloat(cake.base_weight) || 1;
    const customFee = parseFloat(cake.custom_design_fee) || 0;
    
    const breakdown = {
      base: basePrice,
      customFee: customFee,
      flavour: 0
    };

    if (weight && flavour) {
      const selectedWeight = parseFloat(weight);
      let cakePrice = basePrice;
      
      if (selectedWeight > baseWeight) {
        cakePrice = basePrice + (((selectedWeight - baseWeight) / 0.5) * (((basePrice / baseWeight) / 2) * 0.75));
      } else if (selectedWeight < baseWeight) {
        cakePrice = basePrice * (selectedWeight / baseWeight);
      }
      breakdown.base = cakePrice;
      
      const selectedFlavour = JSON.parse(flavour);
      // For special cakes, we do not include flavour price based on weight
      breakdown.flavour = (cake.is_special === 1) ? 0 : (parseFloat(selectedFlavour.price_per_half_kg) * (selectedWeight / 0.5));
    }

    return breakdown;
  };

  const handleAddToCart = () => {
    if (!weight || !flavour) {
      // Show error toast if weight/flavour not selected
      if (window.plugins && window.plugins.toast) {
        window.plugins.toast.showShortCenter('Please select weight and flavour');
      } else {
        alert('Please select weight and flavour');
      }
      return;
    }

    const selectedFlavour = JSON.parse(flavour);

    addCake({
      cakeId: cake.id,
      categoryId: cake.category_id,
      cakeTitle: cake.title,
      cakeImage: cake.image,
      weight: parseFloat(weight),
      flavourId: selectedFlavour.id,
      flavourName: selectedFlavour.flavour_name,
      flavourPrice: selectedFlavour.price_per_half_kg,
      basePrice: cake.base_price,
      baseWeight: cake.base_weight,
      customDesignFee: cake.custom_design_fee || 0,
      quantity: quantity,
      cakeMessage: cakeMessage.trim() // Add cake message
    });

    // Show success toast
    if (window.plugins && window.plugins.toast) {
      // Use Cordova toast plugin on device
      window.plugins.toast.showShortCenter('🎉 Cake added to cart!');
    } else {
      // Use custom toast component for browser
      setToastMessage('🎉 Cake added to cart!');
    }
    
    // Navigate to cart after a short delay
    setTimeout(() => {
      navigate('/cart');
    }, 500);
  };

  if (loading) {
    return (
      <div className="cake-details-page">
        <div className="details-header glass-effect">
          <Link to="/" className="back-button ripple">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </Link>
          <h1 className="details-title">Loading...</h1>
        </div>
        <div className="loading-container">
          <div className="skeleton" style={{ height: '300px', marginBottom: '1rem' }}></div>
          <div className="skeleton" style={{ height: '40px', marginBottom: '1rem' }}></div>
          <div className="skeleton" style={{ height: '80px' }}></div>
        </div>
      </div>
    );
  }

  if (!cake) {
    return null;
  }

  const totalPrice = calculatePrice();
  const breakdown = getPriceBreakdown();
  const imageUrl = getImageUrl(cake.image);

  // Generate weight options
  const weightOptions = [];
  const startWeight = cake ? Math.max(0.5, parseFloat(cake.base_weight) || 0.5) : 0.5;
  
  for (let w = startWeight; w <= 10; w += 0.5) {
    weightOptions.push(w);
  }

  return (
    <div className="cake-details-page">
      <div className="details-header glass-effect">
        <Link to="/" className="back-button ripple">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
          </svg>
        </Link>
        <h1 className="details-title">{cake.title}</h1>
      </div>

      <div className="details-content">
        {imageUrl && (
          <div className="details-image-container">
            <img src={imageUrl} alt={cake.title} className="details-image" loading="lazy" />
            {cake.custom_design_fee > 0 && (
              <div className="custom-badge">
                <span>✨ Custom Design</span>
              </div>
            )}
            <img 
              src="/Veg_symbol.png" 
              alt="Veg" 
              className="details-veg-badge" 
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}

        <div className="details-info">
          <div className="cake-header-info">
            <h2 className="details-cake-title">{cake.title}</h2>
            {cake.description && (
            <div className="description-section">
              <p className="description-text">{cake.description}</p>
            </div>
          )}
            <div className="cake-price-badge changeable-updated-amount">
              ₹{Math.round(totalPrice).toLocaleString()}
              {cake.custom_design_fee > 0 && (
                <span className="custom-fee-text">+₹{cake.custom_design_fee} custom</span>
              )}
            </div>
          </div>

          

          <div className="specs-row">
            <div className="spec-badge">
              <svg className="spec-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"></path>
              </svg>
              <span>{weight ? parseFloat(weight).toFixed(1) : cake.base_weight}kg</span>
            </div>
            <div className="spec-badge">
              <svg className="spec-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
              <span>{weight ? `${(parseFloat(weight) / 0.5) * 4} - ${(parseFloat(weight) / 0.5) * 5} People` : cake.serves_people}</span>
            </div>
            {/*<div className="spec-badge">
              <span>{cake.category_name}</span>
            </div>*/}
          </div>

          <div className="selection-section">
            <h3 className="section-heading">Customize Your Order</h3>
            
{cake.is_special === 1 ? (
              <div className="special-cake-info glass-effect">
                <p><strong>Note:</strong> This is a special edition cake.</p>
                <div className="weight-selection-container">
                  <div className="weight-label">Select Weight</div>
                  <div className="weight-options-list">
                    {weightOptions.map(w => (
                      <button 
                        key={w} 
                        className={`weight-option-btn ${parseFloat(weight) === w ? 'active' : ''}`}
                        onClick={() => setWeight(w.toString())}
                      >
                        {w.toFixed(1)} Kg
                      </button>
                    ))}
                  </div>
                  {weight && (
                    <div className="serving-info">
                      {(parseFloat(weight) / 0.5) * 4} - {(parseFloat(weight) / 0.5) * 5} People
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="standard-selection-container">
                <div className="weight-selection-container">
                    <div className="weight-label">Select Weight</div>
                    <div className="weight-options-list">
                      {weightOptions.map(w => (
                        <button 
                          key={w} 
                          className={`weight-option-btn ${parseFloat(weight) === w ? 'active' : ''}`}
                          onClick={() => setWeight(w.toString())}
                        >
                          {w.toFixed(1)} Kg
                        </button>
                      ))}
                    </div>
                  {weight && (
                      <div className="serving-info">
                        {(parseFloat(weight) / 0.5) * 4} - {(parseFloat(weight) / 0.5) * 5} People
                      </div>
                    )}
                </div>

                <div className="weight-selection-container">
                  <div className="weight-label">Select Flavour</div>
                  <div className="weight-options-list">
                    {cake.flavours && cake.flavours.map(f => {
                      let isSelected = false;
                      try {
                        if (flavour) {
                          const parsed = JSON.parse(flavour);
                          isSelected = parsed.id === f.id;
                        }
                      } catch (e) {}

                      return (
                        <button 
                          key={f.id} 
                          className={`weight-option-btn ${isSelected ? 'active' : ''}`}
                          onClick={() => setFlavour(JSON.stringify(f))}
                        >
                          {f.flavour_name}
                          {f.price_per_half_kg > 0 && <span style={{fontSize: '0.75rem', display: 'block', fontWeight: 'normal'}}>+₹{f.price_per_half_kg}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <div className="cake-message-section">
              <h4 className="message-label">Cake Message</h4>
              <input
                type="text"
                className="cake-message-input"
                placeholder="Write A Sweet Wish!"
                value={cakeMessage}
                onChange={(e) => setCakeMessage(e.target.value)}
                maxLength={50}
              />
            </div>

            {(weight || flavour) && (
              <div className="price-summary base-price">
                <div className="price-row">
                  <span>Base Price</span>
                  <span>₹{Math.round(cake.base_price).toLocaleString()}</span>
                </div>
                
                {breakdown.customFee > 0 && (
                  <div className="price-row">
                    <span>Custom Design</span>
                    <span>₹{Math.round(breakdown.customFee).toLocaleString()}</span>
                  </div>
                )}
                
                {breakdown.flavour > 0 && (
                  <div className="price-row">
                    <span>Flavour Charge per 500gm</span>
                    <span>₹{Math.round(breakdown.flavour).toLocaleString()}</span>
                  </div>
                )}
                
                <div className="price-total">
                  <span>Total</span>
                  <span className="total-amount">₹{Math.round(totalPrice).toLocaleString()}</span>
                </div>
              </div>
            )}

            <button className="btn-add-to-cart btn-primary ripple" onClick={handleAddToCart}>  
              Add to Cart
            </button>
          </div>
        </div>
      </div>
      
      {/* Related Cakes Section */}
      {relatedCakes.length > 0 && (
        <div className="related-cakes-section" style={{ padding: '0 1rem 4rem 1rem', maxWidth: '1200px', margin: '0 auto' }}>
          <h3 className="section-heading" style={{ 
            fontSize: '1.25rem', 
            fontWeight: 700, 
            color: '#4a312d', 
            marginBottom: '1rem',
            paddingTop: '2rem',
            borderTop: '1px solid #eee'
          }}>
            You May Also Like
          </h3>
          <div className="cakes-grid" style={{ 
             display: 'flex', 
             overflowX: 'auto',
             gap: '1rem',
             paddingBottom: '1rem',
             scrollbarWidth: 'none',
             msOverflowStyle: 'none'
          }}>
            <style>{`
              .related-cakes-section .cakes-grid::-webkit-scrollbar {
                display: none;
              }
              .related-cakes-section .cakes-grid > * {
                min-width: 160px;
                max-width: 180px;
                flex-shrink: 0;
              }
            `}</style>
            {relatedCakes.map(relatedCake => (
              <CakeCard 
                key={relatedCake.id} 
                cake={relatedCake} 
                onAddToCart={() => handleRelatedCakeClick(relatedCake)}
              />
            ))}
          </div>
        </div>
      )}

      
      {/* Toast notification for browser */}
      {toastMessage && (
        <Toast 
          message={toastMessage} 
          onClose={() => setToastMessage(null)}
        />
      )}
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CakeDetails;
